/** Progress save/restore functions provided by the UI layer. */
export type ProgressHandlers = {
  /** Save current progress. Should handle errors gracefully. */
  saveProgress: (assetId: string, positionSec: number, durationSec: number) => Promise<void>;
  /** Load previous progress. Return null if none exists. */
  loadProgress: (assetId: string) => Promise<{ positionSec: number } | null>;
};

export type ProgressContext = {
  progressHandlers: ProgressHandlers | null;
  assetId: string | null;
  lastProgressSave: number;
  pendingProgressSave: NodeJS.Timeout | null;
};

export function scheduleProgressSave(
  progressCtx: ProgressContext,
  currentTime: number,
  duration: number,
  emitError: (error: string) => void,
): void {
  if (!progressCtx.progressHandlers || !progressCtx.assetId || duration < 15) return; // Skip short assets

  const now = Date.now();
  const timeSinceLastSave = now - progressCtx.lastProgressSave;
  const minInterval = 10000; // 10 seconds minimum

  if (timeSinceLastSave < minInterval) return;

  // Cancel pending save if exists
  if (progressCtx.pendingProgressSave) {
    clearTimeout(progressCtx.pendingProgressSave);
    progressCtx.pendingProgressSave = null;
  }

  // Schedule new save with small delay to batch rapid updates
  progressCtx.pendingProgressSave = setTimeout(() => {
    void saveProgressWithRetry(progressCtx, currentTime, duration, emitError);
    progressCtx.pendingProgressSave = null;
  }, 1000);
}

async function saveProgressWithRetry(
  progressCtx: ProgressContext,
  currentTime: number,
  duration: number,
  emitError: (error: string) => void,
): Promise<void> {
  if (!progressCtx.progressHandlers || !progressCtx.assetId) return;

  const maxRetries = 2;
  let attempts = 0;

  while (attempts <= maxRetries) {
    try {
      await progressCtx.progressHandlers.saveProgress(progressCtx.assetId, currentTime, duration);
      progressCtx.lastProgressSave = Date.now();
      return;
    } catch (error) {
      attempts++;
      if (attempts > maxRetries) {
        emitError(`Progress save failed after ${maxRetries} retries: ${String(error)}`);
        return;
      }
      // Exponential backoff: 1s, 2s delays
      await new Promise((resolve) => setTimeout(resolve, 1000 * attempts));
    }
  }
}

export async function restoreProgress(
  progressCtx: ProgressContext,
  video: HTMLVideoElement,
  emitTimeupdate: (currentTime: number, duration: number) => void,
  emitError: (error: string) => void,
): Promise<void> {
  if (!progressCtx.progressHandlers || !progressCtx.assetId) {
    return;
  }

  try {
    const saved = await progressCtx.progressHandlers.loadProgress(progressCtx.assetId);

    if (!saved || saved.positionSec <= 0) {
      return;
    }

    // Check if video duration is available
    const duration = Number.isFinite(video.duration) ? video.duration : 0;

    // If duration not available yet, wait for loadedmetadata event
    if (!duration || duration === 0) {
      await new Promise<void>((resolve) => {
        const onMetadata = () => {
          video.removeEventListener("loadedmetadata", onMetadata);
          resolve();
        };
        video.addEventListener("loadedmetadata", onMetadata);
        // Timeout after 5 seconds
        setTimeout(() => {
          video.removeEventListener("loadedmetadata", onMetadata);
          resolve();
        }, 5000);
      });
    }

    // Re-check duration after waiting
    const finalDuration = Number.isFinite(video.duration) ? video.duration : 0;

    // Don't restore if near the end (within 2 seconds)
    if (finalDuration > 0 && saved.positionSec >= finalDuration - 2) {
      return;
    }

    // Restore position
    video.currentTime = saved.positionSec;
    emitTimeupdate(saved.positionSec, finalDuration || 0);
  } catch (error) {
    emitError(`Progress restore failed: ${String(error)}`);
  }
}
